import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import {useQuery} from 'react-query'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  useEffect(() => {
    if (currentUser?.has_active_time_tracking === false)
      document.title = `TaskRush - ${process.env.REACT_APP_THEME_NAME}`
  }, [currentUser?.has_active_time_tracking])

  useEffect(() => {
    if (currentUser?.has_active_time_tracking) {
      const intervalId = window.setInterval(
        () =>
          (document.title =
            document.title === `TaskRush - ${process.env.REACT_APP_THEME_NAME}`
              ? '▶ Tarefa em andamento'
              : `TaskRush - ${process.env.REACT_APP_THEME_NAME}`),
        1500
      )
      return () => window.clearInterval(intervalId)
    }
  })

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = async () => {
    try {
      // Chamar API de logout para limpar last_activity_at no backend
      const auth = authHelper.getAuth()
      if (auth?.api_token) {
        await authHelper.callLogoutAPI()
      }
    } catch (error) {
      // Continuar com logout local mesmo se API falhar
      console.error('Erro ao fazer logout na API:', error)
    } finally {
      // Limpar dados locais
      saveAuth(undefined)
      setCurrentUser(undefined)
    }
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  
  // If no auth token, show content immediately (user will be redirected to login)
  useEffect(() => {
    if (!auth?.api_token) {
      setShowSplashScreen(false)
    }
  }, [auth])
  
  const {data} = useQuery(
    ['verify-token', auth?.api_token],
    async () => {
      return (await getUserByToken(auth?.api_token!)).data
    },
    {
      enabled: !!auth?.api_token, // Only run if token exists
      retry: false,
      cacheTime: 0,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess(data) {
        setCurrentUser(data)
        setShowSplashScreen(false)
      },
      onError() {
        setShowSplashScreen(false)
      },
    }
  )

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
